// Data loading. The browser never models: it polls meta.json and fetches published JSON.
//
// - meta.json is polled every 15 s with If-None-Match (304 = unchanged).
// - snapshots/{id}/** are immutable: cached in memory (only the two newest snapshot ids are kept).
// - history/{race}.json and notes.json are fetched with ?v=<snapshot_id | notes_updated> so the
//   edge cache can never hand us a copy older than the meta that pointed at it.
// - Failures never clear data: views keep rendering the last good copy.

import type { Geometry } from 'geojson';
import type { Backtest, Diagnostics, EarlyVote, History, Meta, Notes, Overview, RaceFile, Timeline, Turnout } from '../types';
import { Lru } from './timeline';

export const POLL_MS = 15_000;
const HIDDEN_POLL_MS = 60_000;
// A request that never settles (a stalled connection) must not stop updates: meta polls give up after 10 s, data
// files after 25 s, and the next poll tries again.
const META_TIMEOUT_MS = 10_000;
const FILE_TIMEOUT_MS = 25_000;

/** fetch + read the body within `ms` (the timer also covers a body that stalls mid-download). */
async function timedFetch<T>(url: string, init: RequestInit, ms: number, read: (r: Response) => Promise<T>): Promise<T> {
  const ctl = typeof AbortController === 'undefined' ? null : new AbortController();
  const timer = ctl ? setTimeout(() => ctl.abort(), ms) : undefined;
  try {
    return await read(await fetch(url, ctl ? { ...init, signal: ctl.signal } : init));
  } finally {
    clearTimeout(timer);
  }
}
export const STALE_MS = 5 * 60_000;
const FAILS_FOR_STALE = 3; // ~45 s of failed meta polls
const BACKTEST_TTL = 5 * 60_000;

export class HttpError extends Error {
  constructor(public status: number, public path: string) {
    super(`HTTP ${status} for ${path}`);
  }
}

/** Why the page may be showing out-of-date results (null = fresh). `since` = ms epoch of the last good signal. */
export interface StaleInfo {
  kind: 'fetch' | 'heartbeat' | 'feed';
  since: number | null;
}

/** Stale threshold in ms: `?stale=<seconds>` (in the query or the hash query) overrides the 300 s default. */
export function staleThresholdMs(): number {
  const read = (qs: string) => new URLSearchParams(qs).get('stale');
  const v = Number(read(location.search) ?? read(location.hash.split('?')[1] ?? ''));
  return Number.isFinite(v) && v > 0 ? v * 1000 : STALE_MS;
}

export interface StoreEvent {
  metaChanged: boolean;
  snapshotChanged: boolean;
}

export interface GeoFeature {
  type: 'Feature';
  properties: { unit_id: string; name?: string; [k: string]: unknown };
  geometry: Geometry;
}
export interface GeoCollection {
  type: 'FeatureCollection';
  features: GeoFeature[];
  placeholder?: boolean;
}

function validMeta(m: unknown): m is Meta {
  const x = m as Meta;
  return !!x && typeof x === 'object' && x.schema_version === 1 && typeof x.last_publish === 'string' && !!x.health && !!x.election;
}

export class Store {
  readonly base: string;
  meta: Meta | null = null;
  /** Consecutive failed meta polls (network, 5xx, 429, invalid JSON). */
  metaFailures = 0;
  /** server clock - client clock (ms), only applied when the skew is large. */
  clockOffset = 0;

  private metaEtag: string | null = null;
  private listeners = new Set<(e: StoreEvent) => void>();
  private immutable = new Map<string, Promise<unknown>>();
  /** Snapshot ids whose files stay cached (most recently used last); the live snapshot is always kept. */
  private snapLru = new Lru<string>(8);
  private versioned = new Map<string, { v: string; etag: string | null; promise: Promise<unknown> }>();
  private geo = new Map<string, Promise<GeoCollection | null>>();
  private backtest: { at: number; promise: Promise<Backtest | null> } | null = null;
  private timer: number | undefined;
  private pollSeq = 0;
  private lastNotes: Notes | null = null;
  private lastEarly: EarlyVote | null = null;
  private nextDelay = POLL_MS;

  /** Where shared, dataset-independent files live (geo layers, backtest). Defaults to `base`. */
  readonly sharedBase: string;

  constructor(base = '/data/', opts: { sharedBase?: string } = {}) {
    this.base = base;
    this.sharedBase = opts.sharedBase ?? base;
  }

  /** Static datasets (replays): meta is fetched once, never polled, never stale. */
  get isStatic(): boolean {
    return !!this.meta?.static;
  }

  start() {
    void this.poll();
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !this.isStatic) this.schedule(250);
    });
    window.addEventListener('online', () => { if (!this.isStatic) this.schedule(250); });
  }

  subscribe(fn: (e: StoreEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  now(): number {
    return Date.now() + this.clockOffset;
  }

  get snapshotId(): string | null {
    return this.meta?.snapshot_id ?? null;
  }

  /**
   * Stale when (in priority order): meta.json keeps failing; the heartbeat (last_publish) is older than the
   * threshold; or the feed is red and the last successful ENR poll (last_poll_ok) is older than the threshold
   * (the publisher is alive but cannot reach the state's results site).
   */
  get staleInfo(): StaleInfo | null {
    const m = this.meta;
    if (m?.static) return null;
    const T = staleThresholdMs();
    const t = (iso: string | null | undefined) => { const v = iso ? Date.parse(iso) : NaN; return Number.isFinite(v) ? v : null; };
    if (this.metaFailures >= FAILS_FOR_STALE) return { kind: 'fetch', since: t(m?.last_publish) };
    if (!m) return null;
    const hb = t(m.last_publish);
    if (hb !== null && this.now() - hb > T) return { kind: 'heartbeat', since: hb };
    const okAt = t(m.last_poll_ok);
    if (m.health.status === 'red' && (okAt === null || this.now() - okAt > T)) return { kind: 'feed', since: okAt };
    return null;
  }

  private emit(e: StoreEvent) {
    for (const fn of this.listeners) {
      try {
        fn(e);
      } catch (err) {
        console.error(err);
      }
    }
  }

  private schedule(ms: number) {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.poll(), ms);
  }

  private async poll() {
    const sentAt = Date.now();
    // a poll started meanwhile (tab shown again, back online) supersedes this one: never apply an older answer
    const my = ++this.pollSeq;
    let ev: StoreEvent = { metaChanged: false, snapshotChanged: false };
    try {
      const headers: Record<string, string> = {};
      if (this.metaEtag) headers['If-None-Match'] = this.metaEtag;
      const got = await timedFetch(this.url('meta.json'), { headers, cache: 'no-store' }, META_TIMEOUT_MS,
        async (r) => ({ res: r, body: r.status === 200 ? ((await r.json()) as unknown) : null }));
      if (my !== this.pollSeq) return;
      const res = got.res;
      this.nextDelay = document.hidden ? HIDDEN_POLL_MS : POLL_MS;
      this.trackClock(res, sentAt);
      if (res.status === 304 && this.meta) {
        this.ok();
      } else if (res.ok) {
        const next: unknown = got.body;
        if (!validMeta(next)) throw new Error('meta.json failed validation');
        const prev = this.meta;
        this.metaEtag = res.headers.get('ETag');
        this.meta = next;
        ev = {
          metaChanged: !prev || JSON.stringify(prev) !== JSON.stringify(next),
          snapshotChanged: !prev || prev.snapshot_id !== next.snapshot_id,
        };
        if (ev.snapshotChanged && next.snapshot_id) this.touchSnapshot(next.snapshot_id);
        this.ok();
      } else {
        if (res.status === 429) {
          const ra = Number(res.headers.get('Retry-After'));
          this.nextDelay = Math.max(this.nextDelay, Number.isFinite(ra) ? ra * 1000 : 30_000);
        }
        throw new HttpError(res.status, 'meta.json');
      }
    } catch {
      if (my !== this.pollSeq) return;
      this.nextDelay = document.hidden ? HIDDEN_POLL_MS : POLL_MS;
      this.metaFailures++;
      // Back off gently while failing, but keep trying (max 60 s).
      this.nextDelay = Math.max(this.nextDelay, Math.min(60_000, POLL_MS * Math.min(4, this.metaFailures)));
    }
    this.emit(ev);
    if (this.isStatic && this.metaFailures === 0) return; // a static dataset never changes: no more polling
    // Jitter spreads synchronized viewers (e.g. after a broadcast mention).
    this.schedule(this.nextDelay + Math.round((Math.random() - 0.5) * 2000));
  }

  private ok() {
    this.metaFailures = 0;
  }

  private trackClock(res: Response, sentAt: number) {
    const d = Date.parse(res.headers.get('Date') ?? '');
    if (!Number.isFinite(d)) return;
    const offset = d + 500 - (sentAt + Date.now()) / 2;
    // Date has 1 s resolution: ignore small skews, correct big ones (wrong device clock).
    this.clockOffset = Math.abs(offset) > 30_000 ? offset : 0;
  }

  private url(key: string) {
    return (key.startsWith('geo/') || key.startsWith('diagnostics/backtest') ? this.sharedBase : this.base) + key;
  }

  private async fetchJSON<T>(key: string, etag?: string | null): Promise<{ status: 200; data: T; etag: string | null } | { status: 304 }> {
    const headers: Record<string, string> = {};
    if (etag) headers['If-None-Match'] = etag;
    return timedFetch(this.url(key), { headers, cache: etag ? 'no-store' : 'default' }, FILE_TIMEOUT_MS, async (res) => {
      if (res.status === 304) return { status: 304 as const };
      if (!res.ok) throw new HttpError(res.status, key);
      return { status: 200 as const, data: (await res.json()) as T, etag: res.headers.get('ETag') };
    });
  }

  /** Mark a snapshot as recently used; drop cached files of snapshots that fell out of the LRU. */
  private touchSnapshot(sid: string) {
    const live = this.snapshotId;
    for (const old of this.snapLru.touch(sid, (k) => k === live)) {
      for (const k of this.immutable.keys()) if (k.split('/')[1] === old) this.immutable.delete(k);
    }
  }

  /** Immutable snapshot file, e.g. snapshotFile<Overview>('overview.json'). */
  snapshotFile<T>(rel: string, sid: string): Promise<T> {
    const key = `snapshots/${sid}/${rel}`;
    this.touchSnapshot(sid);
    let p = this.immutable.get(key) as Promise<T> | undefined;
    if (!p) {
      p = this.fetchJSON<T>(key).then((r) => (r as { data: T }).data);
      this.immutable.set(key, p);
      p.catch(() => this.immutable.delete(key));
    }
    return p;
  }

  overview(sid: string) {
    return this.snapshotFile<Overview>('overview.json', sid);
  }
  race(sid: string, raceId: string) {
    return this.snapshotFile<RaceFile>(`races/${encodeURIComponent(raceId)}.json`, sid);
  }
  turnout(sid: string) {
    return this.snapshotFile<Turnout>('turnout.json', sid);
  }
  diagnostics(sid: string) {
    return this.snapshotFile<Diagnostics>('diagnostics.json', sid);
  }

  /** Mutable file re-fetched when `v` changes; a 304 reuses the previous copy. */
  private versionedFile<T>(key: string, v: string): Promise<T> {
    const cur = this.versioned.get(key);
    if (cur && cur.v === v) return cur.promise as Promise<T>;
    const prevPromise = cur?.promise as Promise<T> | undefined;
    const entry = { v, etag: cur?.etag ?? null, promise: undefined as unknown as Promise<T> };
    entry.promise = this.fetchJSON<T>(`${key}?v=${encodeURIComponent(v)}`, entry.etag).then(async (r) => {
      if (r.status === 304) {
        if (!prevPromise) throw new Error(`304 without cached ${key}`);
        return prevPromise;
      }
      entry.etag = r.etag;
      return r.data;
    });
    entry.promise.catch(() => {
      if (this.versioned.get(key) === entry) {
        if (cur) this.versioned.set(key, cur);
        else this.versioned.delete(key);
      }
    });
    this.versioned.set(key, entry);
    return entry.promise;
  }

  history(raceId: string, sid: string) {
    return this.versionedFile<History>(`history/${encodeURIComponent(raceId)}.json`, sid);
  }

  /** Index of every published snapshot (for replay). null if the publisher doesn't write one (404). */
  timeline(): Promise<Timeline | null> {
    const v = this.meta?.snapshot_id;
    if (!v) return Promise.resolve(null);
    return this.versionedFile<Timeline>('timeline.json', v).catch((e) => {
      if (e instanceof HttpError && e.status === 404) return null;
      throw e;
    });
  }

  /** SEC early-voting / absentee / registration figures (+ an optional model block). null if not published. */
  earlyVote(): Promise<EarlyVote | null> {
    const m = this.meta;
    if (!m) return Promise.resolve(null);
    const v = m.snapshot_id ?? m.last_publish.slice(0, 16); // before the first snapshot: refresh about once a minute
    return this.versionedFile<EarlyVote>('early_vote.json', v).then((d) => (this.lastEarly = d)).catch((e) => {
      if (e instanceof HttpError && e.status === 404) return this.lastEarly; // a brief 404 doesn't blank what we had
      throw e;
    });
  }

  notes(): Promise<Notes | null> {
    const v = this.meta?.notes_updated;
    if (!v) return Promise.resolve(null);
    return this.versionedFile<Notes>('notes.json', v).then((d) => (this.lastNotes = d)).catch((e) => {
      if (e instanceof HttpError && e.status === 404) return this.lastNotes;
      throw e;
    });
  }

  /** GeoJSON layer; resolves null when the layer is missing (404) so the map can hide itself. */
  geoLayer(layer: string): Promise<GeoCollection | null> {
    let p = this.geo.get(layer);
    if (!p) {
      p = this.fetchJSON<GeoCollection>(`geo/${encodeURIComponent(layer)}.geojson`)
        .then((r) => (r as { data: GeoCollection }).data)
        .catch((e) => {
          if (e instanceof HttpError && e.status === 404) {
            setTimeout(() => this.geo.delete(layer), 300_000); // maybe published later: look again in 5 min
            return null;
          }
          this.geo.delete(layer); // transient: retry next time
          throw e;
        });
      this.geo.set(layer, p);
    }
    return p;
  }

  backtestFile(): Promise<Backtest | null> {
    if (this.backtest && Date.now() - this.backtest.at < BACKTEST_TTL) return this.backtest.promise;
    const promise = this.fetchJSON<Backtest>('diagnostics/backtest.json')
      .then((r) => (r as { data: Backtest }).data)
      .catch((e) => {
        if (e instanceof HttpError && e.status === 404) return null;
        this.backtest = null;
        throw e;
      });
    this.backtest = { at: Date.now(), promise };
    return promise;
  }
}
